import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Newspaper, FileText, Video, Image as ImageIcon, ExternalLink } from 'lucide-react';

const typeMeta = {
  pdf: { icon: FileText, label: 'PDF', color: 'bg-red-50 text-red-700' },
  video: { icon: Video, label: 'Video', color: 'bg-blue-50 text-blue-700' },
  image: { icon: ImageIcon, label: 'Imagen', color: 'bg-amber-50 text-amber-700' },
  link: { icon: ExternalLink, label: 'Enlace', color: 'bg-gray-100 text-gray-700' },
};

const emptyForm = { title: '', description: '', content_type: 'pdf', content_url: '' };

export default function AdminIntranet() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.getIntranet();
      setPosts(r.data);
    } catch (e) { toast.error('Error al cargar anuncios'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Título requerido'); return; }
    setLoading(true);
    try {
      if (editingId) {
        await api.updateIntranet(editingId, form);
        toast.success('Anuncio actualizado');
      } else {
        await api.createIntranet(form);
        toast.success('Anuncio publicado');
      }
      setForm(emptyForm);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setLoading(false); }
  };

  const handleEdit = (p) => {
    setForm({
      title: p.title || '',
      description: p.description || '',
      content_type: p.content_type || 'link',
      content_url: p.content_url || '',
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`¿Eliminar el anuncio "${title}"?`)) return;
    try {
      await api.deleteIntranet(id);
      toast.success('Anuncio eliminado');
      load();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-MX') : '-';

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="admin-intranet-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <Newspaper className="h-7 w-7 text-[#DC143C]" />
              Intranet
            </h1>
            <p className="text-gray-600 mt-1">Publica anuncios, políticas y avisos para el personal</p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-intranet-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Nuevo Anuncio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight">{editingId ? 'Editar Anuncio' : 'Nuevo Anuncio'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm font-medium uppercase tracking-wide">Título *</Label>
                  <Input data-testid="intranet-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Política de Calidad 2026" className="h-11" required />
                </div>
                <div>
                  <Label className="text-sm font-medium uppercase tracking-wide">Descripción</Label>
                  <textarea data-testid="intranet-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 border border-gray-300 rounded-md h-24" placeholder="Breve resumen visible para el personal..." />
                </div>
                <div>
                  <Label className="text-sm font-medium uppercase tracking-wide">Tipo de contenido</Label>
                  <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                    <SelectTrigger data-testid="intranet-type-select" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF (Drive)</SelectItem>
                      <SelectItem value="video">Video (Drive / URL directa)</SelectItem>
                      <SelectItem value="image">Imagen (URL)</SelectItem>
                      <SelectItem value="link">Enlace externo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium uppercase tracking-wide">URL del contenido</Label>
                  <Input data-testid="intranet-url-input" value={form.content_url} onChange={(e) => setForm({ ...form, content_url: e.target.value })} placeholder="https://drive.google.com/..." className="h-11" />
                  <p className="text-xs text-gray-500 mt-1">
                    Para Drive, comparte el archivo como "Cualquiera con el enlace" y pega la URL aquí. Se incrustará automáticamente.
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-intranet-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">{loading ? 'Guardando...' : 'Guardar'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {posts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Newspaper className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No hay anuncios publicados. Crea el primero con "Nuevo Anuncio".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Publicado</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map((p) => {
                    const meta = typeMeta[p.content_type] || typeMeta.link;
                    const Icon = meta.icon;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 transition-colors" data-testid="admin-intranet-row">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{p.title}</p>
                          {p.description && <p className="text-xs text-gray-500 line-clamp-1 mt-1">{p.description}</p>}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium uppercase ${meta.color}`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{fmt(p.created_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} data-testid="edit-intranet-button" className="text-gray-700 hover:bg-gray-100">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id, p.title)} data-testid="delete-intranet-button" className="text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
